import { notificationLink, openNotificationLink } from '../utils/notificationLink';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNotificationsStore } from '../store/notifications';
import { cn } from '../utils/cn';
import { EmailPrefsPanel } from './EmailPrefsPanel';

// =============================================================================
// Header notification bell (W5 IA).
//
// 좌측 네비의 "알림 센터" 페이지를 헤더 우측 벨 아이콘 + 드롭다운 패널로
// 대체한다. 전역 접근 + 미읽음 배지. 기존 useNotificationsStore 를 그대로
// 재사용한다(읽음/모두읽음/필터). 백엔드 오프라인 시 store 가 빈 목록을
// 반환하므로 안전하게 마운트된다.
// =============================================================================

// 메뉴 카테고리 배지 — 알림 type → 표시 라벨 (Figma 1-1).
const CATEGORY_LABEL: Record<string, string> = {
  media: '미디어 관리',
  schedule: '스케줄 관리',
  'schedule.approval.request': '승인 요청',
  'scope.approval.request': '승인 요청',
  'scope.approval.result': '승인 결과',
  device: '디바이스 관리',
  bo: '조직 관리',
  h9: 'H9',
};

// 알림 날짜 포맷 (Figma 1-3): 방금 전 / N분 전 / N시간 전 / 어제 / YYYY. MM. DD
function formatNotifiedAt(iso: string): string {
  const then = new Date(iso);
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - then.getTime()) / 1000);
  if (diffSec < 60) return '방금 전';
  if (diffSec < 60 * 60) return `${Math.floor(diffSec / 60)}분 전`;
  if (diffSec < 24 * 60 * 60) return `${Math.floor(diffSec / 3600)}시간 전`;
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(midnight.getTime() - 24 * 60 * 60 * 1000);
  if (then >= yesterday && then < midnight) return '어제';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${then.getFullYear()}. ${p(then.getMonth() + 1)}. ${p(then.getDate())}`;
}

export function NotificationsBell(): JSX.Element {
  const items = useNotificationsStore((s) => s.items);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const loading = useNotificationsStore((s) => s.loading);
  const error = useNotificationsStore((s) => s.error);
  const load = useNotificationsStore((s) => s.load);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  const [open, setOpen] = useState(false);
  // ⚙ 메일 알림 설정 — 목록 자리에 잠깐 끼워 넣고, 패널을 닫으면 다시 목록으로.
  const [settingsOpen, setSettingsOpen] = useState(false);
  useEffect(() => { if (!open) setSettingsOpen(false); }, [open]);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Initial load + light polling so the badge stays fresh without a page.
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="알림"
        title="알림"
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            if (next) void load();
            return next;
          });
        }}
        className="relative inline-flex h-[36px] w-[36px] items-center justify-center rounded border border-line bg-panel-2 text-text-dim transition-colors hover:border-line-strong hover:text-text"
      >
        <BellGlyph />
        {unreadCount > 0 && (
          <span
            aria-label={`읽지 않은 알림 ${unreadCount}건`}
            className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red"
          />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[46px] z-50 w-[408px] overflow-hidden rounded-md border border-line-strong bg-panel shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
          <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
            <span className="flex items-center gap-1.5 text-[16px] font-semibold text-text">
              {settingsOpen ? '메일 알림 설정' : `알림${loading ? ' · 로딩 중…' : ''}`}
              {/* ⚙ — 알림을 보는 자리에서 "메일로도 받기"를 켠다(2026-09-22 대교). 누르면 목록 ↔ 설정 전환. */}
              <button
                type="button"
                aria-label={settingsOpen ? '알림 목록으로' : '메일 알림 설정'}
                aria-pressed={settingsOpen}
                title={settingsOpen ? '알림 목록으로' : '메일 알림 설정'}
                onClick={() => setSettingsOpen((v) => !v)}
                className={cn('inline-flex h-6 w-6 items-center justify-center rounded text-text-dim hover:bg-panel-2 hover:text-text',
                  settingsOpen && 'bg-panel-2 text-text')}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            </span>
            {settingsOpen ? (
              // 설정에서 나가는 길 — ⚙ 재클릭만으로는 막다른 길이라 같은 자리에 '알림으로'를 둔다.
              <button
                type="button"
                onClick={() => setSettingsOpen(false)}
                className="rounded border border-line px-2 py-0.5 text-[12px] text-text-dim hover:border-line-strong hover:text-text"
              >
                ← 알림으로
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void markAllRead()}
                disabled={unreadCount === 0}
                className="rounded border border-line px-2 py-0.5 text-[12px] text-text-dim hover:border-line-strong hover:text-text disabled:opacity-40"
              >
                모두 읽음
              </button>
            )}
          </div>

          {error && (
            <div role="alert" className="border-b border-red/40 bg-red/15 px-3 py-2 text-[14px] text-red">
              불러오기 실패: {error}
            </div>
          )}

          {settingsOpen ? <EmailPrefsPanel /> : <ul className="max-h-[432px] divide-y divide-line overflow-auto">
            {items.length === 0 ? (
              <li className="px-4 py-8 text-center text-[14px] text-text-dim">
                {loading ? '불러오는 중…' : '알림이 없습니다.'}
              </li>
            ) : (
              items.slice(0, 30).map((n) => {
                const unread = n.readAt === null;
                const category = CATEGORY_LABEL[n.type] ?? (n.type.startsWith('device.') || n.type.startsWith('fleet.device.') ? '디바이스 관리' : '기타');
                return (
                  <li
                    key={n.id}
                    onClick={() => {
                      if (unread) void markRead(n.id);
                      // 링크가 있으면 그 화면으로 — 승인 요청은 승인 관리, 그 밖의 편성 알림은 편성 정보가 열린다.
                      if (n.link) { setOpen(false); openNotificationLink(n.link); }
                    }}
                    className={cn(
                      'flex items-center gap-2.5 px-3 py-2.5',
                      (unread || n.link) && 'cursor-pointer',
                      unread && 'bg-accent-soft/40',
                    )}
                  >
                    {/* 읽지 않음 red dot (Figma) */}
                    <span
                      className={cn('h-1.5 w-1.5 flex-none rounded-full', unread ? 'bg-red' : 'bg-transparent')}
                      aria-hidden
                    />
                    {/* 메뉴 카테고리 배지 */}
                    <span className="flex-none rounded border border-line bg-panel-2 px-1.5 py-0.5 font-mono text-[11px] text-text-dim">
                      {category}
                    </span>
                    {/* 알림 내용 — 최대 1줄 말줄임. 말줄임된 경우에만 hover Tooltip
                        (Figma 1-2 + 코멘트: 전체 메시지 확인 수단). */}
                    <TruncatedMessage
                      full={n.body ? `${n.title} ${n.body}` : n.title}
                      className={cn('min-w-0 flex-1 truncate text-[14px]', unread ? 'font-semibold text-text' : 'text-text-dim')}
                    >
                      {n.title}
                      {n.body && <span className="text-text-dim font-normal"> {n.body}</span>}
                    </TruncatedMessage>
                    {/* 알림 날짜 */}
                    <span className="ml-auto flex-none font-mono text-[12px] text-text-dimmer">
                      {formatNotifiedAt(n.createdAt)}
                    </span>
                  </li>
                );
              })
            )}
          </ul>}
        </div>
      )}
    </div>
  );
}

// 말줄임된 경우에만 hover 말풍선을 띄우는 1줄 텍스트 (Figma 1-2 정의).
// 말풍선은 InfoTooltip과 동일한 룩 — body 포탈 + fixed라 패널 overflow에 안 잘림.
function TruncatedMessage({
  full,
  className,
  children,
}: {
  full: string;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = (): void => {
    const el = ref.current;
    if (!el) return;
    // 실제로 말줄임된 경우에만 노출.
    if (el.scrollWidth <= el.clientWidth) return;
    const b = el.getBoundingClientRect();
    const half = 170; // 말풍선 max-w 340의 절반 — 화면 밖 클램프
    const cx = Math.min(Math.max(b.left + b.width / 2, half + 8), window.innerWidth - half - 8);
    setPos({ top: b.bottom + 8, left: cx });
  };

  return (
    <span
      ref={ref}
      className={className}
      onMouseEnter={show}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos &&
        createPortal(
          <span
            role="tooltip"
            style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%)', zIndex: 100000 }}
            className="pointer-events-none w-max max-w-[340px] whitespace-pre-line rounded-md bg-[#2b2b2b] px-3 py-2 text-[13px] font-normal leading-snug text-white shadow-lg"
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: -5,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderBottom: '5px solid #2b2b2b',
              }}
            />
            {full}
          </span>,
          document.body,
        )}
    </span>
  );
}

function BellGlyph(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5.5 8.5a4.5 4.5 0 119 0c0 3 1 4.5 1.5 5h-12c.5-.5 1.5-2 1.5-5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M8.3 16.5a1.8 1.8 0 003.4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
